import React, { useEffect, useState } from 'react';
import { Bell, Download, Sparkles, X } from 'lucide-react';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { usePushNotification } from '../contexts/PushNotificationContext';
import './PushEngagementPrompt.css';

const STORAGE_KEY = 'cep_push_prompt_dismissed_at';
const DISMISS_DAYS = 7;

/**
 * Bannière : installer l’app + activer les notifications (offres, nouveautés).
 */
const PushEngagementPrompt = () => {
  const { currentUser } = useAuth();
  const { language } = useLanguage();
  const { permission, subscription, requestPermission, subscribeToPush } = usePushNotification();
  const fr = language === 'fr';

  const [visible, setVisible] = useState(false);
  const [busy, setBusy] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [isStandalone, setIsStandalone] = useState(false);

  useEffect(() => {
    const standalone =
      window.matchMedia('(display-mode: standalone)').matches ||
      window.navigator.standalone === true;
    setIsStandalone(standalone);

    const dismissedAt = Number(localStorage.getItem(STORAGE_KEY) || 0);
    const coolDownMs = DISMISS_DAYS * 24 * 60 * 60 * 1000;
    const cooled = !dismissedAt || Date.now() - dismissedAt > coolDownMs;

    const onBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', onBeforeInstall);

    // Afficher si pas encore abonné, ou si install possible
    const needsPush =
      'Notification' in window &&
      permission !== 'granted' &&
      !subscription;
    if (cooled && (needsPush || !standalone)) {
      const t = setTimeout(() => setVisible(true), 2500);
      return () => {
        clearTimeout(t);
        window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      };
    }

    return () => window.removeEventListener('beforeinstallprompt', onBeforeInstall);
  }, [permission, subscription]);

  if (!visible) return null;
  if (permission === 'granted' && subscription && isStandalone && !deferredPrompt) {
    return null;
  }

  const dismiss = () => {
    localStorage.setItem(STORAGE_KEY, String(Date.now()));
    setVisible(false);
  };

  const enablePush = async () => {
    if (!currentUser) {
      toast.error(fr ? 'Connectez-vous pour activer les notifications.' : 'Sign in to enable notifications.');
      return;
    }
    setBusy(true);
    try {
      const ok = await requestPermission();
      if (!ok) {
        toast.error(
          fr
            ? 'Permission refusée. Activez les notifications dans les réglages du navigateur.'
            : 'Permission denied. Enable notifications in browser settings.'
        );
        return;
      }
      const subscribed = await subscribeToPush();
      if (subscribed) {
        toast.success(
          fr
            ? 'Parfait ! Vous recevrez offres, nouveautés et avantages.'
            : 'Great! You’ll get offers, news and perks.'
        );
        if (isStandalone || !deferredPrompt) dismiss();
      } else {
        toast.error(fr ? 'Impossible d’activer les notifications.' : 'Could not enable notifications.');
      }
    } finally {
      setBusy(false);
    }
  };

  const installApp = async () => {
    if (!deferredPrompt) {
      toast(
        fr
          ? 'Sur mobile : menu du navigateur → « Ajouter à l’écran d’accueil ».'
          : 'On mobile: browser menu → “Add to Home Screen”.'
      );
      return;
    }
    deferredPrompt.prompt();
    const choice = await deferredPrompt.userChoice;
    setDeferredPrompt(null);
    if (choice?.outcome === 'accepted') {
      toast.success(fr ? 'Application installée !' : 'App installed!');
    }
  };

  return (
    <div className="cep-push-prompt" role="dialog" aria-label={fr ? 'Notifications' : 'Notifications'}>
      <button type="button" className="cep-push-prompt__close" onClick={dismiss} aria-label="Fermer">
        <X size={18} />
      </button>
      <div className="cep-push-prompt__icon" aria-hidden="true">
        <Sparkles size={22} />
      </div>
      <div className="cep-push-prompt__copy">
        <strong>
          {fr ? 'Ne ratez rien à Conakry' : 'Don’t miss out in Conakry'}
        </strong>
        <p>
          {fr
            ? 'Recevez les offres, avantages, nouvelles entreprises et billets — directement sur votre téléphone.'
            : 'Get offers, perks, new businesses and tickets — straight on your phone.'}
        </p>
      </div>
      <div className="cep-push-prompt__actions">
        {(!subscription || permission !== 'granted') && (
          <button type="button" className="cep-push-prompt__btn" disabled={busy} onClick={enablePush}>
            <Bell size={16} />
            {fr ? 'Activer les alertes' : 'Enable alerts'}
          </button>
        )}
        {!isStandalone && (
          <button type="button" className="cep-push-prompt__btn cep-push-prompt__btn--ghost" onClick={installApp}>
            <Download size={16} />
            {fr ? 'Installer l’app' : 'Install app'}
          </button>
        )}
      </div>
    </div>
  );
};

export default PushEngagementPrompt;
